import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@heroui/react';
import { ErrorFallback } from '@/components/errorBoundary';
import { getNodeTestIdProp } from '@/utils';
import { ShiftCloseModal } from './ShiftCloseModal';
import { ShiftLedgerSummaryModal } from './ShiftLedgerSummaryModal';
import { ShiftRegisterFilterBar } from './ShiftRegisterFilterBar';
import { ShiftRegisterSkeleton } from './ShiftRegisterSkeleton';
import { ShiftRegisterTable } from './ShiftRegisterTable';
import type { ShiftRegisterLogic } from './useShiftRegister';

type ShiftRegisterViewProps = ShiftRegisterLogic;

/**
 * The shift register (render-only): the filters, which stay usable while the
 * list loads, the list in whichever state the hook reports, the ledger summary
 * modal and the close-shift modal.
 */
export const ShiftRegisterView: React.FC<ShiftRegisterViewProps> = ({
    filters,
    list,
    summary,
    close,
}) => {
    const { t } = useTranslation('pages');

    return (
        <section
            className="flex flex-col gap-4"
            {...getNodeTestIdProp('shift-register')}
        >
            <ShiftRegisterFilterBar {...filters} />

            {list.status === 'error' ? (
                <ErrorFallback
                    data-testid="shift-register-load-error"
                    id="shift-register-load-error"
                    message={t('shifts.errorMessage')}
                    onRetry={list.onRetry}
                    retryLabel={t('shifts.errorRetry')}
                    title={t('shifts.errorTitle')}
                />
            ) : null}

            {list.status === 'loading' ? <ShiftRegisterSkeleton /> : null}

            {list.status === 'empty' || list.status === 'ready' ? (
                <>
                    {list.refresh === 'error' ? (
                        <ErrorFallback
                            data-testid="shift-register-refresh-error"
                            id="shift-register-refresh-error"
                            message={t('shifts.refreshErrorMessage')}
                            onRetry={list.onRetry}
                            retryLabel={t('shifts.errorRetry')}
                            title={t('shifts.errorTitle')}
                        />
                    ) : null}
                    <Card className="overflow-hidden p-0">
                        <ShiftRegisterTable
                            onCloseShift={close.onStart}
                            onOpen={summary.onOpen}
                            rows={list.status === 'ready' ? list.data : []}
                        />
                    </Card>
                </>
            ) : null}

            <ShiftLedgerSummaryModal {...summary} />
            <ShiftCloseModal {...close} />
        </section>
    );
};
